<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import ReportHeader from './components/ReportHeader.vue';
import V4Button from 'dashboard/components-next/button/Button.vue';
import { useRoute } from 'vue-router';

const route = useRoute();
const rules = ref([]);
const runs = ref([]);
const failedDeliveries = ref(0);
const loading = ref(true);
const error = ref('');
const { t } = useI18n();

const endpoint = computed(
  () => `/api/v2/accounts/${route.params.accountId}/reports/billing_automation`
);

const totals = computed(() =>
  runs.value.reduce(
    (result, run) => {
      result.clients += Number(run.clients_processed || 0);
      result.invoices += Number(run.invoices_found || 0);
      result.sent += Number(run.sent_count || 0);
      result.queued += Number(run.queued_count || 0);
      result.duplicates += Number(run.duplicate_count || 0);
      result.failed += Number(run.failed_count || 0);
      return result;
    },
    { clients: 0, invoices: 0, sent: 0, queued: 0, duplicates: 0, failed: 0 }
  )
);

const ruleName = ruleId =>
  rules.value.find(rule => Number(rule.id) === Number(ruleId))?.name ||
  t('REPORT.BILLING_AUTOMATION.DEFAULT_RULE');

const statusLabel = status =>
  ({
    completed: t('REPORT.BILLING_AUTOMATION.STATUS.COMPLETED'),
    running: t('REPORT.BILLING_AUTOMATION.STATUS.RUNNING'),
    queued: t('REPORT.BILLING_AUTOMATION.STATUS.QUEUED'),
    failed: t('REPORT.BILLING_AUTOMATION.STATUS.FAILED'),
  })[status] || status;

const statusClass = status => ({
  'text-green-600 bg-green-500/10': status === 'completed',
  'text-amber-600 bg-amber-500/10': ['running', 'queued'].includes(status),
  'text-red-600 bg-red-500/10': status === 'failed',
});

const formatDate = value => {
  if (!value) return '--';
  return new Intl.DateTimeFormat('pt-BR', {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(new Date(value));
};

const load = async () => {
  loading.value = true;
  error.value = '';
  try {
    const response = await window.axios.get(endpoint.value);
    rules.value = response.data.rules || [];
    runs.value = response.data.runs || [];
    failedDeliveries.value = Number(response.data.failed_deliveries || 0);
  } catch {
    error.value = t('REPORT.BILLING_AUTOMATION.ERRORS.LOAD');
  } finally {
    loading.value = false;
  }
};

onMounted(load);
</script>

<template>
  <ReportHeader
    :header-title="t('REPORT.BILLING_AUTOMATION.TITLE')"
    :header-description="t('REPORT.BILLING_AUTOMATION.DESCRIPTION')"
  >
    <V4Button
      :label="t('COMMON.REFRESH')"
      icon="i-lucide-refresh-cw"
      size="sm"
      :is-loading="loading"
      @click="load"
    />
  </ReportHeader>

  <div class="mx-auto flex max-w-5xl flex-col gap-4 pb-6">
    <div
      v-if="error"
      class="rounded-xl border border-red-500/30 bg-red-500/10 p-4 text-sm text-red-600"
    >
      {{ error }}
    </div>

    <section class="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-5">
      <div
        v-for="metric in [
          {
            label: t('REPORT.BILLING_AUTOMATION.METRICS.CLIENTS'),
            value: totals.clients,
            icon: 'i-lucide-users',
          },
          {
            label: t('REPORT.BILLING_AUTOMATION.METRICS.INVOICES'),
            value: totals.invoices,
            icon: 'i-lucide-receipt-text',
          },
          {
            label: t('REPORT.BILLING_AUTOMATION.METRICS.SENT'),
            value: totals.sent,
            icon: 'i-lucide-send',
          },
          {
            label: t('REPORT.BILLING_AUTOMATION.METRICS.QUEUED'),
            value: totals.queued,
            icon: 'i-lucide-clock-3',
          },
          {
            label: t('REPORT.BILLING_AUTOMATION.METRICS.DUPLICATES'),
            value: totals.duplicates,
            icon: 'i-lucide-shield-check',
          },
        ]"
        :key="metric.label"
        class="rounded-xl border border-n-slate-5 bg-n-surface-2 p-4"
      >
        <div class="mb-3 flex items-center gap-2 text-sm text-n-slate-11">
          <span class="text-lg text-n-blue-9" :class="metric.icon" />
          {{ metric.label }}
        </div>
        <div class="text-2xl font-semibold text-n-slate-12">
          {{ Number(metric.value).toLocaleString('pt-BR') }}
        </div>
      </div>
    </section>

    <section class="rounded-xl border border-n-slate-5 bg-n-surface-2 p-5">
      <div class="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 class="text-lg font-semibold text-n-slate-12">
            {{ t('REPORT.BILLING_AUTOMATION.RECENT_TITLE') }}
          </h2>
          <p class="text-sm text-n-slate-11">
            {{ t('REPORT.BILLING_AUTOMATION.RECENT_DESCRIPTION') }}
          </p>
        </div>
        <span class="rounded-full bg-red-500/10 px-3 py-1 text-xs text-red-600">
          {{
            t('REPORT.BILLING_AUTOMATION.PENDING_FAILURES', {
              count: failedDeliveries.toLocaleString(),
            })
          }}
        </span>
      </div>

      <div v-if="loading" class="py-10 text-center text-sm text-n-slate-11">
        {{ t('REPORT.BILLING_AUTOMATION.LOADING') }}
      </div>
      <div
        v-else-if="!runs.length"
        class="rounded-lg border border-dashed border-n-slate-5 p-8 text-center text-sm text-n-slate-11"
      >
        {{ t('REPORT.BILLING_AUTOMATION.EMPTY') }}
      </div>
      <div v-else class="overflow-x-auto">
        <table class="w-full min-w-[760px] text-left text-sm">
          <thead
            class="border-b border-n-slate-5 text-xs uppercase text-n-slate-11"
          >
            <tr>
              <th class="px-3 py-3 font-medium">
                {{ t('REPORT.BILLING_AUTOMATION.TABLE.RULE') }}
              </th>
              <th class="px-3 py-3 font-medium">
                {{ t('REPORT.BILLING_AUTOMATION.TABLE.RUN') }}
              </th>
              <th class="px-3 py-3 font-medium">
                {{ t('REPORT.BILLING_AUTOMATION.TABLE.STATUS') }}
              </th>
              <th class="px-3 py-3 font-medium">
                {{ t('REPORT.BILLING_AUTOMATION.TABLE.CLIENTS') }}
              </th>
              <th class="px-3 py-3 font-medium">
                {{ t('REPORT.BILLING_AUTOMATION.TABLE.INVOICES') }}
              </th>
              <th class="px-3 py-3 font-medium">
                {{ t('REPORT.BILLING_AUTOMATION.TABLE.SENT') }}
              </th>
              <th class="px-3 py-3 font-medium">
                {{ t('REPORT.BILLING_AUTOMATION.TABLE.QUEUED') }}
              </th>
              <th class="px-3 py-3 font-medium">
                {{ t('REPORT.BILLING_AUTOMATION.TABLE.DUPLICATES') }}
              </th>
            </tr>
          </thead>
          <tbody class="divide-y divide-n-slate-5">
            <tr v-for="run in runs" :key="run.id" class="text-n-slate-12">
              <td class="px-3 py-3 font-medium">
                {{ ruleName(run.billing_automation_rule_id) }}
              </td>
              <td class="whitespace-nowrap px-3 py-3 text-n-slate-11">
                {{ formatDate(run.created_at) }}
              </td>
              <td class="px-3 py-3">
                <span
                  class="rounded-full px-2 py-1 text-xs font-medium"
                  :class="statusClass(run.status)"
                >
                  {{ statusLabel(run.status) }}
                </span>
              </td>
              <td class="px-3 py-3">{{ run.clients_processed || 0 }}</td>
              <td class="px-3 py-3">{{ run.invoices_found || 0 }}</td>
              <td class="px-3 py-3">{{ run.sent_count || 0 }}</td>
              <td class="px-3 py-3">{{ run.queued_count || 0 }}</td>
              <td class="px-3 py-3">{{ run.duplicate_count || 0 }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>
  </div>
</template>
